import { ReactNode, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Trash2, Loader2, CreditCard, QrCode, Tag, Coins, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/hooks/useCart";
import { useCartTotals } from "@/hooks/useCartTotals";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { cn } from "@/lib/utils";
import { CourseRecommendations } from "@/components/recommendations/CourseRecommendations";
import { PurchaseTermsCheckbox } from "@/components/checkout/PurchaseTermsCheckbox";
import { PURCHASE_TERMS_VERSION } from "@/components/checkout/PurchaseTermsDialog";
import { PaymentBadges } from "@/components/payments/PaymentBadges";
import pixLogo from "@/assets/pix-logo.svg";

interface CartDrawerProps {
  trigger?: ReactNode;
}

type PaymentMethod = "card" | "pix";

const PIX_CODES_STORAGE_KEY = "cart_pix_promo_codes";

export function CartDrawer({ trigger }: CartDrawerProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Payment-method feature flags (admin-controlled)
  const { data: stripeEnabled = false } = useFeatureFlag("payment_stripe_enabled");
  const { data: pixEnabled = false } = useFeatureFlag("payment_pix_enabled");
  // Default selection prefers Pix when available, else card
  const [method, setMethod] = useState<PaymentMethod>("pix");
  // Force-correct selection when flags load / change
  useEffect(() => {
    if (method === "card" && !stripeEnabled && pixEnabled) {
      setMethod("pix");
    } else if (method === "pix" && !pixEnabled && stripeEnabled) {
      setMethod("card");
    }
  }, [stripeEnabled, pixEnabled, method]);
  const noMethodAvailable = !stripeEnabled && !pixEnabled;
  const onlyOneMethod = stripeEnabled !== pixEnabled;

  // Draft inputs (not yet applied to pricing)
  const [discountInput, setDiscountInput] = useState("");
  const [coinInput, setCoinInput] = useState("");
  // Applied codes (passed to pricing engine + checkout)
  const [appliedDiscount, setAppliedDiscount] = useState<string | undefined>(undefined);
  const [appliedCoin, setAppliedCoin] = useState<string | undefined>(undefined);

  const { items, count, cartId, subtotal: clientSubtotal, remove, clear, isLoading } = useCart();
  const { data: priced, isFetching: pricingLoading } = useCartTotals({
    cartId,
    discountCode: appliedDiscount,
    silvaCoinCode: appliedCoin,
    enabled: open && count > 0,
  });
  const totals = priced?.totals;

  const applyDiscount = () => {
    const v = discountInput.trim().toUpperCase();
    if (!v) return;
    setAppliedDiscount(v);
  };
  const clearDiscount = () => {
    setAppliedDiscount(undefined);
    setDiscountInput("");
  };
  const applyCoin = () => {
    const v = coinInput.trim().toUpperCase();
    if (!v) return;
    setAppliedCoin(v);
  };
  const clearCoin = () => {
    setAppliedCoin(undefined);
    setCoinInput("");
  };

  const handleCheckout = async () => {
    if (!cartId) return;
    if (!termsAccepted) {
      toast({
        title: "Aceite os termos",
        description: "Você precisa concordar com os Termos de Compra, Acesso e Reembolso.",
        variant: "destructive",
      });
      return;
    }
    const termsAcceptedAt = new Date().toISOString();
    if (method === "pix") {
      try {
        sessionStorage.setItem(
          PIX_CODES_STORAGE_KEY,
          JSON.stringify({
            discountCode: appliedDiscount,
            silvaCoinCode: appliedCoin,
            termsAcceptedAt,
            termsVersion: PURCHASE_TERMS_VERSION,
          }),
        );
      } catch {/* ignore */}
      setOpen(false);
      navigate("/checkout/pix-cart");
      return;
    }
    setCheckingOut(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-cart-checkout", {
        body: {
          cartId,
          discountCode: appliedDiscount,
          silvaCoinCode: appliedCoin,
          termsAcceptedAt,
          termsVersion: PURCHASE_TERMS_VERSION,
        },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Stripe URL ausente");
      }
    } catch (err: any) {
      toast({
        title: "Não foi possível iniciar o checkout",
        description: err?.message ?? "Tente novamente",
        variant: "destructive",
      });
      setCheckingOut(false);
    }
  };

  if (!user) return null;

  const fmt = (n: number) =>
    n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            aria-label="Abrir carrinho"
            className={cn(
              "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
              "border glass-border glass-surface text-foreground/80",
              "hover:[background-color:hsl(var(--glass-tint)/0.08)] hover:text-foreground transition-colors"
            )}
          >
            <ShoppingBag className="h-[18px] w-[18px]" />
            {count > 0 && (
              <Badge
                variant="default"
                className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full text-[10px] tabular-nums"
              >
                {count}
              </Badge>
            )}
          </button>
        )}
      </SheetTrigger>

      <SheetContent side="right" className="flex w-full sm:max-w-md flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Seu carrinho
            {count > 0 && (
              <Badge variant="secondary" className="ml-1">
                {count}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            Cursos selecionados para compra. O total final é calculado no checkout.
          </SheetDescription>
        </SheetHeader>

        <Separator />

        <ScrollArea className="flex-1">
          <div className="px-6 py-4 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ShoppingBag className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground mb-4">
                  Seu carrinho está vazio.
                </p>
                <Button asChild variant="outline" size="sm" onClick={() => setOpen(false)}>
                  <Link to="/courses">Explorar cursos</Link>
                </Button>
              </div>
            ) : (
              items.map((item) => {
                const c = item.course;
                return (
                  <div
                    key={item.id}
                    className="flex gap-3 rounded-lg border border-border/60 bg-card/50 p-3"
                  >
                    <Link
                      to={c ? `/courses/${c.slug}` : "#"}
                      onClick={() => setOpen(false)}
                      className="shrink-0"
                    >
                      <div className="h-16 w-20 overflow-hidden rounded-md bg-muted">
                        {c?.thumbnail_url ? (
                          <img
                            src={c.thumbnail_url}
                            alt={c.title}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : null}
                      </div>
                    </Link>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={c ? `/courses/${c.slug}` : "#"}
                        onClick={() => setOpen(false)}
                        className="block truncate text-sm font-medium hover:text-primary transition-colors"
                      >
                        {c?.title ?? "Curso"}
                      </Link>
                      <p className="mt-1 text-sm text-foreground/70">
                        {c?.is_free
                          ? "Grátis"
                          : c?.price
                            ? fmt(Number(c.price))
                            : "—"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(item.id)}
                      aria-label="Remover do carrinho"
                      className="self-start text-muted-foreground hover:text-destructive transition-colors p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })
            )}

            {/* Cross-sell recommendations */}
            {(items.length > 0 || (!isLoading && items.length === 0)) && (
              <div className="pt-2">
                <CourseRecommendations
                  context="cart"
                  variant="list"
                  limit={3}
                  className="border-dashed bg-muted/20"
                />
              </div>
            )}
          </div>
        </ScrollArea>

        {items.length > 0 && (
          <>
            <Separator />
            <SheetFooter className="px-6 py-4 flex-col gap-2 sm:flex-col sm:items-stretch sm:space-x-0">
              {/* Promo / Silva Coin inputs */}
              <div className="space-y-2 pb-1">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") applyDiscount(); }}
                      placeholder="Cupom"
                      maxLength={50}
                      disabled={!!appliedDiscount}
                      className="h-9 pl-7 text-xs uppercase"
                    />
                  </div>
                  {appliedDiscount ? (
                    <Button variant="outline" size="sm" className="h-9" onClick={clearDiscount}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="h-9" onClick={applyDiscount} disabled={!discountInput.trim()}>
                      Aplicar
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Coins className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={coinInput}
                      onChange={(e) => setCoinInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") applyCoin(); }}
                      placeholder="Silva Coin"
                      maxLength={50}
                      disabled={!!appliedCoin}
                      className="h-9 pl-7 text-xs uppercase"
                    />
                  </div>
                  {appliedCoin ? (
                    <Button variant="outline" size="sm" className="h-9" onClick={clearCoin}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="h-9" onClick={applyCoin} disabled={!coinInput.trim()}>
                      Aplicar
                    </Button>
                  )}
                </div>
              </div>

              <Separator className="my-1" />

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">
                  {fmt(totals?.subtotal ?? clientSubtotal)}
                </span>
              </div>
              {totals && totals.bundle_discount > 0 && (
                <div className="flex items-center justify-between text-sm text-primary">
                  <span>Desconto de bundle</span>
                  <span className="tabular-nums">−{fmt(totals.bundle_discount)}</span>
                </div>
              )}
              {totals && totals.promo_discount > 0 && (
                <div className="flex items-center justify-between text-sm text-primary">
                  <span>Promoção</span>
                  <span className="tabular-nums">−{fmt(totals.promo_discount)}</span>
                </div>
              )}
              {totals && totals.code_discount > 0 && (
                <div className="flex items-center justify-between text-sm text-primary">
                  <span>Cupom {appliedDiscount}</span>
                  <span className="tabular-nums">−{fmt(totals.code_discount)}</span>
                </div>
              )}
              {totals && appliedDiscount && totals.code_discount === 0 && !pricingLoading && (
                <p className="text-[11px] text-destructive">Cupom inválido ou não aplicável.</p>
              )}
              {totals && totals.silva_coin_discount > 0 && (
                <div className="flex items-center justify-between text-sm text-primary">
                  <span>Silva Coin {appliedCoin}</span>
                  <span className="tabular-nums">−{fmt(totals.silva_coin_discount)}</span>
                </div>
              )}
              {totals && appliedCoin && totals.silva_coin_discount === 0 && !pricingLoading && (
                <p className="text-[11px] text-destructive">Silva Coin inválido ou já utilizado.</p>
              )}
              <Separator className="my-1" />
              <div className="flex items-center justify-between text-base">
                <span className="font-medium">Total</span>
                <span className="font-semibold tabular-nums">
                  {fmt(totals?.total ?? clientSubtotal)}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Total validado pelo servidor.
              </p>

              {/* Payment method selector — gated by admin feature flags */}
              {noMethodAvailable ? (
                <div className="rounded-lg border border-border bg-muted/40 p-3 text-center text-xs text-muted-foreground">
                  Pagamentos temporariamente indisponíveis. Tente novamente em breve.
                </div>
              ) : onlyOneMethod ? null : (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {stripeEnabled && (
                    <button
                      type="button"
                      onClick={() => setMethod("card")}
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-lg border p-3 text-xs transition-colors relative",
                        method === "card"
                          ? "border-primary bg-primary/5 text-foreground"
                          : "border-border bg-card text-muted-foreground hover:text-foreground"
                      )}
                      aria-pressed={method === "card"}
                    >
                      <CreditCard className="h-4 w-4" />
                      <span className="font-medium">Cartão</span>
                      <span className="absolute -top-1.5 -right-1.5 rounded-full bg-amber-500/90 px-1.5 py-0.5 text-[9px] font-semibold text-white shadow-sm">
                        em testes
                      </span>
                    </button>
                  )}
                  {pixEnabled && (
                    <button
                      type="button"
                      onClick={() => setMethod("pix")}
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-lg border p-3 text-xs transition-colors",
                        method === "pix"
                          ? "border-primary bg-primary/5 text-foreground"
                          : "border-border bg-card text-muted-foreground hover:text-foreground"
                      )}
                      aria-pressed={method === "pix"}
                    >
                      <QrCode className="h-4 w-4" />
                      <span className="font-medium">Pix</span>
                    </button>
                  )}
                </div>
              )}

              <PaymentBadges className="justify-center" />

              <PurchaseTermsCheckbox
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
              />

              <Button
                size="lg"
                className="w-full"
                onClick={handleCheckout}
                disabled={
                  checkingOut ||
                  !cartId ||
                  (totals?.total ?? 0) <= 0 ||
                  noMethodAvailable ||
                  !termsAccepted
                }
              >
                {checkingOut ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Redirecionando…
                  </>
                ) : method === "pix" ? (
                  <>
                    <img src={pixLogo} alt="" className="h-5 w-auto mr-2" />
                    Pagar com Pix
                  </>
                ) : (
                  "Finalizar com cartão"
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={() => clear()}
              >
                Limpar carrinho
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
